import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Os dois vídeos da abertura, um atrás do outro, sem som.
 *
 * **Sem som nunca é opcional aqui.** `muted` é o que faz o navegador deixar o
 * vídeo começar sozinho: com áudio, a política de reprodução automática recusa
 * e a tela inicial abriria congelada no primeiro quadro. Os arquivos também
 * saem sem faixa de áudio nenhuma (`pnpm arte`), então não há o que desligar.
 *
 * **A troca é por tempo, não por `ended`.** Esperar o fim para começar o outro
 * deixaria um piscar preto entre os dois. Aqui o segundo começa enquanto o
 * primeiro ainda está tocando, e a passagem é uma transição de opacidade: os
 * dois existem ao mesmo tempo por um segundo e pouco.
 *
 * **A foto continua embaixo.** Ela é o cartaz enquanto o vídeo carrega, é o que
 * aparece para quem pediu menos movimento no sistema, e é o que fica se o
 * vídeo falhar. A tela nunca depende de o vídeo ter dado certo.
 */

const VIDEOS = ["/arte/abertura-1.mp4", "/arte/abertura-2.mp4"];

/** Quanto dura a passagem de um vídeo para o outro. */
const TROCA_MS = 1400;

/** Quando começar a trocar, contando do fim do vídeo que está tocando. */
const ANTECEDENCIA_S = TROCA_MS / 1000;

export function FundoDeAbertura(): ReactNode {
  const refs = [useRef<HTMLVideoElement>(null), useRef<HTMLVideoElement>(null)];
  const [ativo, definirAtivo] = useState(0);
  const [entrou, definirEntrou] = useState(false);
  const ativoRef = useRef(0);

  /*
   * Quem pediu menos movimento no sistema não recebe vídeo nenhum.
   *
   * A escolha é lida uma vez, na montagem: trocar de vídeo para foto no meio da
   * tela seria mais movimento, não menos.
   */
  const [semMovimento] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true,
  );

  useEffect(() => {
    if (semMovimento) return;
    // A entrada acontece no quadro seguinte ao da montagem, senão o navegador
    // desenha o vídeo já opaco e a transição não existe.
    const quadro = requestAnimationFrame(() => definirEntrou(true));
    return () => cancelAnimationFrame(quadro);
  }, [semMovimento]);

  if (semMovimento) return null;

  function aoAndar(indice: number): void {
    if (indice !== ativoRef.current) return;

    const atual = refs[indice]?.current;
    const proximo = refs[1 - indice]?.current;
    if (!atual || !proximo || !Number.isFinite(atual.duration)) return;
    if (atual.duration - atual.currentTime > ANTECEDENCIA_S) return;

    // A marca muda antes de qualquer coisa assíncrona: `timeupdate` dispara
    // várias vezes por segundo, e sem isto a troca seria pedida de novo a cada
    // disparo dentro da janela.
    ativoRef.current = 1 - indice;
    proximo.currentTime = 0;
    void proximo.play().catch(() => undefined);
    definirAtivo(1 - indice);
  }

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: -1,
        opacity: entrou ? 1 : 0,
        transition: `opacity ${TROCA_MS}ms ease`,
      }}
    >
      {VIDEOS.map((fonte, indice) => (
        <video
          key={fonte}
          ref={refs[indice]}
          src={fonte}
          poster="/arte/fundo.webp"
          muted
          playsInline
          preload="auto"
          autoPlay={indice === 0}
          onTimeUpdate={() => aoAndar(indice)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: ativo === indice ? 1 : 0,
            transition: `opacity ${TROCA_MS}ms ease`,
          }}
        />
      ))}

      {/*
        O véu vai por cima do vídeo, e não no fundo da tela.

        É o mesmo escurecimento que a foto já levava. Sem ele aqui, o texto
        branco cairia em cima de arquibancada iluminada em movimento, que é pior
        do que em cima de foto parada.
      */}
      <div className="veu-estadio" />
    </div>
  );
}
